import { api } from "./api.js";
import { useApp } from "./context.js";
import { ItemView } from "./items/ItemView.js";
import { LibraryScreen } from "./Library.js";
import { useServicePicks } from "./menu/picks.js";
import type { ScreenProps } from "./screens.js";

/**
 * Screen 02. The items, or one item when `params.specId` names one of its
 * recipes (#180; a recipe's id is the spec id it replaced, so old links
 * hold). `params.edit === "1"` opens the editor straight away, seeded from
 * the development chat's draft if there is one.
 */
export function RecipesScreen({ params }: ScreenProps) {
  const app = useApp();
  const picks = useServicePicks();
  const recipes = app.library.recipes ?? [];
  const items = app.library.items ?? [];

  if (!params.specId) {
    return (
      <LibraryScreen
        library={app.library}
        overrides={app.overrides}
        picks={picks}
        canEdit={app.me.can["spec:edit"]}
        onOpen={(specId) => app.go({ kind: "recipes", params: { specId } })}
        onNew={async (name, kind) => {
          const { spec } = await api.createItem(app.token, name, kind);
          await app.refreshLibrary();
          app.go({ kind: "recipes", params: { specId: spec.specId, edit: "1" } });
        }}
      />
    );
  }

  const recipe = recipes.find((r) => r.recipeId === params.specId);
  const item = recipe ? items.find((i) => i.itemId === recipe.itemId) : undefined;

  // An empty book is not a bad link. A new organisation has no items at all,
  // and a stale bookmark into it should say so rather than accuse the reader
  // of asking for a recipe that was deleted.
  if (!recipe || !item) {
    return items.length === 0 ? (
      <div className="pad">
        <div className="msg">
          <div style={{ fontWeight: 600 }}>This organisation's book is empty.</div>
          <div style={{ color: "var(--t2)", marginTop: 4 }}>There are no items to open yet.</div>
          <button className="btn" style={{ marginTop: 14 }} onClick={() => app.go({ kind: "recipes" })}>
            Back to the items →
          </button>
        </div>
      </div>
    ) : (
      <div className="pad">
        <div className="msg err">That recipe is not in the book.</div>
      </div>
    );
  }

  return (
    <ItemView
      key={item.itemId}
      item={item}
      recipes={recipes.filter((r) => r.itemId === item.itemId)}
      recipeId={recipe.recipeId}
      startEditing={params.edit === "1"}
      onBack={() => app.go({ kind: "recipes" })}
      onOpenRecipe={(recipeId, edit) =>
        app.go(
          { kind: "recipes", params: edit ? { specId: recipeId, edit: "1" } : { specId: recipeId } },
          edit ? undefined : { replace: params.specId === recipeId },
        )
      }
    />
  );
}
